import React, { useState, useCallback, useMemo, useEffect } from "react";
import SidebarContext from "./SidebarContext";

const SidebarContextProvider = ({
    children,
    entity = null,
    show = false,
    onSave,
    onDelete,
    onDiscard,
    onToggleActive,
    metadata = null,
    initialPage = null,
    // Guidance shown after a successful save. Receives what was saved and returns
    // { heading, subheading, allowAddAnother, steps: [{ label, why, path, to }] }.
    // Returning nothing (or no steps) keeps the old behaviour: save and close.
    nextSteps = null,
}) => {
    const [currentEntity, setCurrentEntity] = useState(entity);
    const [showSidebar, setShowSidebar] = useState(show);
    const [currentPage, setCurrentPage] = useState(initialPage);
    // Initialize runtime and emitted data with entity values on mount
    const [runtimeData, setRuntimeData] = useState(() =>
        entity ? { ...entity } : {},
    );
    const [emittedData, setEmittedData] = useState(() =>
        entity ? { ...entity } : {},
    );
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [pageValidators, setPageValidators] = useState({});
    const [saveOutcome, setSaveOutcome] = useState(null);

    // Sync entity prop with state and reset data when entity changes
    useEffect(() => {
        setCurrentEntity(entity);
        // Only reset data when switching entities or going from entity to no entity or vice versa
        // This prevents losing form data during save operations
        const entityIdChanged = entity?.id !== currentEntity?.id;
        const entityChanged = entity !== currentEntity;
        if (
            entityIdChanged ||
            (entityChanged && (!entity?.id || !currentEntity?.id))
        ) {
            // Initialize runtime and emitted data with entity values
            const initialData = entity ? { ...entity } : {};
            setRuntimeData(initialData);
            setEmittedData(initialData);
        }
    }, [entity, currentEntity]); // Depend on full entity objects

    // Sync show prop with state
    useEffect(() => {
        setShowSidebar(show);
    }, [show]);

    const handleSave = useCallback(async () => {
        if (!onSave) return;

        setIsSaving(true);
        try {
            const isNew = !currentEntity?.id;
            const saved = await onSave(emittedData, currentEntity);
            // Built before the form is cleared — the guidance quotes what was typed.
            const outcome = nextSteps
                ? nextSteps({
                      data: emittedData,
                      entity: currentEntity,
                      isNew,
                      saved,
                  })
                : null;
            // Reset state after successful save
            setRuntimeData({});
            setEmittedData({});
            setCurrentPage(initialPage);
            if (outcome?.steps?.length) {
                setSaveOutcome(outcome);
            }
            // Don't close sidebar here - let parent control via show prop
        } catch (error) {
            console.error("Error saving:", error);
            // Error is already shown by the onSave handler in the parent component
            // Just prevent the error from propagating
        } finally {
            setIsSaving(false);
        }
    }, [onSave, emittedData, currentEntity, initialPage, nextSteps]);

    // Back to a blank form for another one of the same thing.
    const dismissSaveOutcome = useCallback(() => setSaveOutcome(null), []);

    const handleDelete = useCallback(async () => {
        if (!onDelete || !currentEntity) return;

        setIsDeleting(true);
        try {
            await onDelete(currentEntity);
            // Reset state after successful delete
            setRuntimeData({});
            setEmittedData({});
            setCurrentPage(initialPage);
            setSaveOutcome(null);
            // Close sidebar after successful delete
            if (onDiscard) {
                onDiscard();
            }
            setShowSidebar(false);
        } catch (error) {
            console.error("Error deleting:", error);
            throw error;
        } finally {
            setIsDeleting(false);
        }
    }, [onDelete, currentEntity, initialPage, onDiscard]);

    const handleDiscard = useCallback(() => {
        if (onDiscard) {
            onDiscard();
        }
        setShowSidebar(false);
        // Reset state
        setRuntimeData({});
        setEmittedData({});
        setCurrentPage(initialPage);
        setSaveOutcome(null);
    }, [onDiscard, initialPage]);

    const handleNext = useCallback((nextPage) => {
        setCurrentPage(nextPage);
    }, []);

    const registerPageValidator = useCallback((pageName, validatorFn) => {
        setPageValidators((prev) => ({
            ...prev,
            [pageName]: validatorFn,
        }));
    }, []);

    const unregisterPageValidator = useCallback((pageName) => {
        setPageValidators((prev) => {
            const next = { ...prev };
            delete next[pageName];
            return next;
        });
    }, []);

    // Compute overall validity by running all validators
    const isValid = useMemo(() => {
        const validators = Object.values(pageValidators);
        if (validators.length === 0) return true; // No validators = always valid
        return validators.every((validator) => {
            try {
                return validator(emittedData, runtimeData);
            } catch (e) {
                return false;
            }
        });
    }, [pageValidators, emittedData, runtimeData]);

    const contextValue = useMemo(
        () => ({
            entity: currentEntity,
            setEntity: setCurrentEntity,
            show: showSidebar,
            setShow: setShowSidebar,
            currentPage,
            setCurrentPage,
            runtimeData,
            setRuntimeData,
            emittedData,
            setEmittedData,
            isSaving,
            isDeleting,
            pageValidators,
            registerPageValidator,
            unregisterPageValidator,
            isValid,
            saveOutcome,
            dismissSaveOutcome,
            onSave: handleSave,
            onDelete: handleDelete,
            onDiscard: handleDiscard,
            onNext: handleNext,
            onToggleActive,
            metadata,
        }),
        [
            currentEntity,
            showSidebar,
            currentPage,
            runtimeData,
            emittedData,
            isSaving,
            isDeleting,
            pageValidators,
            registerPageValidator,
            unregisterPageValidator,
            isValid,
            saveOutcome,
            dismissSaveOutcome,
            handleSave,
            handleDelete,
            handleDiscard,
            handleNext,
            onToggleActive,
            metadata,
        ],
    );

    return (
        <SidebarContext.Provider value={contextValue}>
            {children}
        </SidebarContext.Provider>
    );
};

export default SidebarContextProvider;
